import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography, Pagination } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { RequisitionService } from '../../services/requisition.service';
import { Requisition, REQUISITION_STATUS_LABELS } from '../../interfaces/requisition.interface';
import ScrollableTable from '../common/ScrollableTable';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const PendingApprovalRequisitions: React.FC = () => {
    const navigate = useNavigate();
    const [requisitions, setRequisitions] = useState<Requisition[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    const handleViewDetails = (id?: string) => {
        navigate(`/pending-approval-requisitions/${id}`);
    };

    const fetchPendingRequisitions = async (page: number = 0) => {
        try {
            setLoading(true);
            const response = await RequisitionService.getPendingApprovalRequisitionsByInstitution(page);
            setRequisitions(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
            setError('');
        } catch (err) {
            setError('Failed to load pending requisitions');
            console.error('Error fetching pending requisitions:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingRequisitions();
    }, []);

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchPendingRequisitions(page - 1);
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="store.pendingApproval.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h4">Pending Approval Requisitions</Typography>
            </Box>

            <div style={{ marginBottom: '20px' }}>
                Total Pending Approval Requisitions: {pageInfo.totalElements}
            </div>

            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', minWidth: 640 }} data-testid="pending-requisitions-table" data-help-section="store.pendingApproval.table">
                <thead>
                    <tr>
                        <th data-help="store.reqList.col.refNo" style={tableHeaderStyle}>Ref No</th>
                        <th data-help="store.reqList.col.date" style={tableHeaderStyle}>Date</th>
                        <th data-help="store.approveReq.col.status" style={tableHeaderStyle}>Status</th>
                        <th data-help="store.reqList.col.createdBy" style={tableHeaderStyle}>Created By</th>
                        <th data-help="store.reqList.col.remarks" style={tableHeaderStyle}>Purpose / remarks</th>
                        <th data-help="store.approveReq.col.actions" style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {requisitions.map((requisition) => (
                        <tr key={requisition.id} data-testid={`requisition-row-${requisition.id}`}>
                            <td style={tableCellStyle}>{requisition.refNo}</td>
                            <td style={tableCellStyle}>
                                {requisition.date ? formatDate(requisition.date) : '-'}
                            </td>
                            <td style={tableCellStyle}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        bgcolor: 'warning.light',
                                        color: 'warning.dark'
                                    }}
                                >
                                    {requisition.status ? REQUISITION_STATUS_LABELS[requisition.status] : '-'}
                                </Box>
                            </td>
                            <td style={tableCellStyle}>{requisition.requisitionByName || '-'}</td>
                            <td style={{ ...tableCellStyle, maxWidth: 280, overflowWrap: 'anywhere' }}>{requisition.remarks || '-'}</td>
                            <td style={tableCellStyle}>
                                <Tooltip title="View Details" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleViewDetails(requisition.id)}
                                        color="primary"
                                        data-testid={`view-button-${requisition.id}`}
                                        data-help="store.pendingApproval.action.view"
                                        aria-label="View Details"
                                    >
                                        <Visibility />
                                    </IconButton>
                                </Tooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </ScrollableTable>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {error && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
                    {error}
                </Box>
            )}
        </div>
    );
};

export default PendingApprovalRequisitions;
